import { useState, useEffect } from 'react';

const cachedStylesheets = [];

/** Asynchronously load a stylesheet on demand inside a functional component.
 *
 * Lightly adapted from https://usehooks.com/useScript/
 */
export const useStyle = (src: string, ready = true) => {
  const [state, setState] = useState({
    loaded: false,
    error: false
  });

  useEffect(
    () => {
      if (!ready) {
        return;
      }

      if (cachedStylesheets.includes(src)) {
        setState({
          loaded: true,
          error: false
        });
      }
      else {
        cachedStylesheets.push(src);

        const stylesheet = document.createElement('link');
        stylesheet.href = src;
        stylesheet.rel = "stylesheet";

        const onStylesheetLoad = () => {
          setState({
            loaded: true,
            error: false
          });
        };

        const onStylesheetError = () => {
          const index = cachedStylesheets.indexOf(src);
          if (index >= 0) cachedStylesheets.splice(index, 1);
          stylesheet.remove();
          setState({
            loaded: true,
            error: true
          });
        };

        stylesheet.addEventListener('load', onStylesheetLoad);
        stylesheet.addEventListener('error', onStylesheetError);

        document.body.appendChild(stylesheet);

        return () => {
          stylesheet.removeEventListener('load', onStylesheetLoad);
          stylesheet.removeEventListener('error', onStylesheetError);
        };
      }
    },
    [src, ready]
  );

  return [state.loaded, state.error];
};
